'use client'

import * as React from 'react'
import { composeEventHandlers } from '@radix-ui/primitive'
import { composeRefs } from '@radix-ui/react-compose-refs'
import { Primitive } from '@radix-ui/react-primitive'
import { Slot } from 'radix-ui'

import { cn } from '@/shared/lib/utils'

import { Button } from './button'

export type InputBaseContextProps = Pick<
	InputBaseProps,
	'autoFocus' | 'disabled'
> & {
	controlRef: React.Ref<HTMLElement> | null
	onFocusedChange: (focused: boolean) => void
}

const InputBaseContext = React.createContext<InputBaseContextProps>({
	autoFocus: false,
	controlRef: null,
	disabled: false,
	onFocusedChange: () => {},
})

const useInputBaseContext = () => React.use(InputBaseContext)

export type InputBaseProps = {
	autoFocus?: boolean
	disabled?: boolean
} & React.ComponentProps<typeof Primitive.div>

export const InputBase = ({
	autoFocus,
	disabled,
	className,
	onClick,
	children,
	...props
}: InputBaseProps) => {
	const [focused, setFocused] = React.useState(false)

	const controlRef = React.useRef<HTMLElement>(null)

	return (
		<InputBaseContext
			value={{
				autoFocus,
				controlRef,
				disabled,
				onFocusedChange: setFocused,
			}}
		>
			<Primitive.div
				onClick={composeEventHandlers(onClick, (event) => {
					// Based on MUI's <InputBase /> implementation.
					// https://github.com/mui/material-ui/blob/master/packages/mui-material/src/InputBase/InputBase.js#L458~L460
					if (controlRef?.current && event.currentTarget === event.target) {
						controlRef.current.focus()
					}
				})}
				className={cn(
					'flex min-h-8 cursor-text items-center gap-2 rounded-lg border border-input bg-input/20 px-3 py-0.5 text-sm transition-colors dark:bg-input/30',
					'aria-invalid:border-destructive aria-invalid:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40',
					'aria-expanded:border-primary data-[state=open]:border-primary',
					'focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/30',
					'focus:border-ring focus:ring-[3px] focus:ring-ring/30 focus:outline-none',
					disabled && 'cursor-not-allowed opacity-50',
					focused && 'ring-[3px] ring-ring/30',
					className
				)}
				{...props}
			>
				{children}
			</Primitive.div>
		</InputBaseContext>
	)
}
InputBase.displayName = 'InputBase'

export const InputBaseFlexWrapper = ({
	className,
	...props
}: React.ComponentProps<typeof Primitive.div>) => (
	<Primitive.div
		className={cn('flex flex-1 flex-wrap', className)}
		{...props}
	/>
)
InputBaseFlexWrapper.displayName = 'InputBaseFlexWrapper'

export const InputBaseControl = ({
	onFocus,
	onBlur,
	ref,
	...props
}: React.ComponentProps<typeof Slot.Root>) => {
	const { controlRef, autoFocus, disabled, onFocusedChange } =
		useInputBaseContext()

	return (
		<Slot.Root
			ref={composeRefs(controlRef, ref)}
			autoFocus={autoFocus}
			onFocus={composeEventHandlers(onFocus, () => onFocusedChange(true))}
			onBlur={composeEventHandlers(onBlur, () => onFocusedChange(false))}
			{...{ disabled }}
			{...props}
		/>
	)
}
InputBaseControl.displayName = 'InputBaseControl'

export type InputBaseAdornmentProps = {
	asChild?: boolean
	disablePointerEvents?: boolean
} & React.ComponentProps<'div'>

export const InputBaseAdornment = ({
	className,
	disablePointerEvents,
	asChild,
	children,
	...props
}: InputBaseAdornmentProps) => {
	const Comp = asChild ? Slot.Root : typeof children === 'string' ? 'p' : 'div'

	const isAction =
		React.isValidElement(children) && children.type === InputBaseAdornmentButton

	return (
		<Comp
			className={cn(
				'flex items-center text-muted-foreground [&_svg]:size-4',
				(!isAction || disablePointerEvents) && 'pointer-events-none',
				className
			)}
			{...props}
		>
			{children}
		</Comp>
	)
}
InputBaseAdornment.displayName = 'InputBaseAdornment'

export const InputBaseAdornmentButton = ({
	type = 'button',
	variant = 'ghost',
	size = 'icon-sm',
	disabled: disabledProp,
	className,
	...props
}: React.ComponentProps<typeof Button>) => {
	const { disabled } = useInputBaseContext()

	return (
		<Button
			type={type}
			variant={variant}
			size={size}
			disabled={disabled || disabledProp}
			className={cn('size-6', className)}
			{...props}
		/>
	)
}

InputBaseAdornmentButton.displayName = 'InputBaseAdornmentButton'

export const InputBaseInput = ({
	className,
	...props
}: React.ComponentProps<typeof Primitive.input>) => (
	<Primitive.input
		className={cn(
			'w-full flex-1 bg-transparent file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-placeholder placeholder-shown:truncate focus:outline-none disabled:pointer-events-none',
			className
		)}
		{...props}
	/>
)
InputBaseInput.displayName = 'InputBaseInput'
